import { fullNamePresenter } from 'utils';
import { secondsToMinutes } from 'date-fns';
import {
  hasStatusForDisableCcmModule,
  hasStatusForDisableRpmModule,
} from 'infrastructure/functions/status-check';
import {
  AppointmentMethod,
  type CcmStatuses,
  type RpmStatuses,
} from 'infrastructure/enums';
import { convertSecondsToTime } from 'utils/timeHelper';

import { CCM_CONSOLE_PAGE_SIZE } from './use-get-table-data';

import type {
  IFollowUpResponseData,
  IFollowUpTableData,
  INewlyTableData,
  INewlyResponseData,
  IPartialsResponseData,
  IPartialsTableData,
  INewlyTableDataFilters,
  TTablePrefix,
  WithoutPrefix,
  IFollowUpTableDataFilters,
  IPartialsTableDataFilters,
  PodFilterValue,
} from 'infrastructure/interfaces';

const checkStatuses = (
  rpmStatus: RpmStatuses,
  ccmStatus: CcmStatuses,
  pcmStatus: boolean,
) => {
  const disableInRpm = hasStatusForDisableRpmModule(rpmStatus);
  const disableInCcm = hasStatusForDisableCcmModule(ccmStatus);

  const statusMap = {
    RPM: !disableInRpm,
    CCM: !pcmStatus && !disableInCcm,
    PCM: pcmStatus,
  };

  return Object.entries(statusMap)
    .filter(([, value]) => value)
    .map(([key]) => key);
};

export const transformNewlyData = (
  data?: INewlyResponseData[],
): INewlyTableData[] => {
  if (!data) return [];

  return data.map((item) => ({
    guid: item.guid,
    provider: item.provider,
    callsMonth: item.callsCount,
    appointmentDate: item.nextAppointment?.scheduledDate ?? null,
    type: checkStatuses(item.status, item.ccmStatus, item.pcmStatus).join('/'),
    dueDate: item.ccmDueDate,
    enrolledDate: item.enrollmentDate,
    calls: item.callsCount,
    lastCallDate: item.lastCCMCall,
    clinicName: item.clinic?.legalname ?? '',
    clinicGuid: item.clinic?.guid ?? null,
    birthDate: item.birthDate,
    language: item.language,
    firstName: item.firstName,
    lastName: item.lastName,
    name: fullNamePresenter({
      title: item.title,
      firstName: item.firstName,
      middleName: item.middleName,
      lastName: item.lastName,
    }),
    tableName: 'newly',
    notes: item.ccmKeyNotes,
    podSection: item.podSection ?? null,
    podName: item.podName ?? null,
    podGuid: item.podGuid ?? null,
  }));
};

export const transformFollowUpData = (
  data?: IFollowUpResponseData[],
): IFollowUpTableData[] => {
  if (!data) return [];
  return data.map((item) => ({
    guid: item.guid,
    actionTimes: convertSecondsToTime(parseInt(item.actionTimes, 10)),
    ccmActionTimes: parseInt(item.actionTimes, 10),
    provider: item.provider,
    callsMonth: item.callsCount,
    lastBilledDate: item.lastCCMBilled,
    appointmentDate: item.nextAppointment?.scheduledDate ?? null,
    smsPreferred: item?.preferredContactMethod === AppointmentMethod.SMS,
    type: checkStatuses(item.status, item.ccmStatus, item.pcmStatus).join('/'),
    dueDate: item.ccmDueDate,
    name: fullNamePresenter({
      title: item.title,
      firstName: item.firstName,
      middleName: item.middleName,
      lastName: item.lastName,
    }),
    firstName: item.firstName,
    lastName: item.lastName,
    tableName: 'follow-up',
    ccmInteractionStatus: item.ccmInteractionStatus,
    birthDate: item.birthDate,
    language: item.language,
    notes: item.ccmKeyNotes,
    contactMethod: item.preferredContactMethod ?? undefined,
    clinicGuid: item.clinic?.guid ?? null,
    podSection: item.podSection ?? null,
    podName: item.podName ?? null,
    podGuid: item.podGuid ?? null,
  }));
};

export const transformPartialsData = (
  data?: IPartialsResponseData[],
): IPartialsTableData[] => {
  if (!data) return [];

  return data.map((item) => ({
    guid: item.guid,
    provider: item.provider,
    callsMonth: item.callsCount,
    appointmentDate: item.nextAppointment?.scheduledDate ?? null,
    type: checkStatuses(item.status, item.ccmStatus, item.pcmStatus).join('/'),
    dueDate: item.ccmDueDate,
    calls: item.callsCount,
    lastCallDate: item.lastCCMCall ?? null,
    name: fullNamePresenter({
      title: item.title,
      firstName: item.firstName,
      middleName: item.middleName,
      lastName: item.lastName,
    }),
    firstName: item.firstName,
    lastName: item.lastName,
    tableName: 'partials',
    minutes: item?.ccmActionTimes
      ? secondsToMinutes(Number(item.ccmActionTimes))
      : 0,
    nextBillable: secondsToMinutes(item.nextBillable),
    birthDate: item.birthDate,
    language: item.language,
    notes: item.ccmKeyNotes,
    clinicGuid: item.clinic?.guid ?? null,
    podSection: item.podSection ?? null,
    podName: item.podName ?? null,
    podGuid: item.podGuid ?? null,
  }));
};

export const removePrefixFromFilter = <
  TFilterWithPrefix extends
    | INewlyTableDataFilters
    | IFollowUpTableDataFilters
    | IPartialsTableDataFilters,
>(
  filter: TFilterWithPrefix,
  prefix: TTablePrefix,
) => {
  type TOutput = WithoutPrefix<TFilterWithPrefix, TTablePrefix>;
  return Object.entries(filter).reduce<TOutput>((acc, [key, value]) => {
    const keyWithoutPrefix = key.replace(prefix, '') as keyof TOutput;

    acc[keyWithoutPrefix] = value as TOutput[keyof TOutput];

    return acc;
  }, {} as TOutput);
};

export const getIsPodFiltered = (
  podFilter: PodFilterValue | undefined,
  allExcluded: boolean,
): boolean => !!podFilter && podFilter !== 'all' && !allExcluded;

export const checkIsPageAvailable = (
  page: number,
  count: number,
  itemsCount: number,
) => {
  if (!page || page < 1) return false;

  if (itemsCount === 0 && page > 1) return false;

  const totalPages = Math.ceil(count / CCM_CONSOLE_PAGE_SIZE);

  return page <= totalPages;
};
